import { VideoQuality } from '@zoom/videosdk';
import { CellLayout } from 'shared/types/video.interface';

export const MAX_NUMBER_PER_PAGE = 2;

const MAX_ROW_COLUMNS = {
  maxColumns: MAX_NUMBER_PER_PAGE,
  maxRows: MAX_NUMBER_PER_PAGE,
};

interface Grid {
  row: number;
  column: number;
}
interface Layout {
  cellWidth: number;
  cellHeight: number;
  cellArea: number;
  column: number;
  row: number;
}

// This thing builts a huge array of candidate grids for all possible candidates amounts (limited to 9)
const layoutCandidates: { [key: number]: Grid[] } = Array.from({ length: 2 })
  // for each candidate count we construct a list of possible grids
  .map((_value, index) => {
    // count is a possible user amount
    const count = index + 1;

    // getting mid, which is a count of a possible column-row ratios
    const mid = Math.ceil(count / 2);

    // constructing grid by different ratios
    const candidates = Array.from({ length: mid })
      // for each grid ratio  we construct a list of possible candidate locations
      .map((_v, i) => {
        // getting row and column amounts
        const row = i + 1;
        const column = Math.ceil(count / row);

        // constructing a pair of possible col-row row-col amounts
        if (row < column) {
          return [
            {
              row,
              column,
            },
            {
              row: column,
              column: row,
            },
          ];
        }
        // if row amount equals column, we have only one variant
        if (row === column) {
          return [
            {
              row,
              column,
            },
          ];
        }
        return [];
      })
      // get all possible ratios with the help of the reducer
      .reduce((prev, curr) => [...prev, ...curr], []);
    // returning the candidate layouts for the grid candidate count
    return { count, candidates };
  })
  // constructing a list for all the candidate counts possible with the help of reducer
  .reduce((prev, curr) => ({ ...prev, [curr.count]: curr.candidates }), {});

// constant description of screen and minimal sizes
const maxCount = 2;
/**
 * This function by the simple formula calculates from screen parameters
 * the counts of rows and columns for page layout
 * @param width video container current wdith
 * @param height video container current heigh
 * @returns an object, describing the maximum count of columns and rows
 */

/**
 * This function calculates the amount of videos displayable on the screen
 * by calculating amount of slots available and then multiplying the rows
 * on columns
 * @param width current viewport width
 * @param height current viewport height
 * @returns number of possible video displayed simultaneously
 */

export function getVideoLayout(rootWidth: number, rootHeight: number, count: number): CellLayout[] {
  /**
   * [1,count]
   */

  if (count > maxCount || count === 0) {
    return [];
  }
  const cellGap = rootWidth < 500 ? 40 : 26;
  // getting maximum rows and columns amount on the page
  let { maxRows, maxColumns } = MAX_ROW_COLUMNS;

  // shrinking those numbers to count of videos
  maxRows = Math.min(maxRows, count);
  maxColumns = Math.min(maxColumns, count);

  // getting the actual count of layout cells needed
  const actualCount = Math.min(count, maxRows * maxColumns);

  // getting the precalculated layout, and getting all the candidate grids for the current
  // candidates count. then shrinking it by our screen restrictions
  const layoutOfCount = layoutCandidates[actualCount].filter(
    (item) => item.row <= maxRows && item.column <= maxColumns,
  );

  // calculating then a layout for given height and width
  // making size of cells maxmimum available
  const preferredLayout: Layout = layoutOfCount
    .map((item) => {
      const { column, row } = item;

      // Calculate available space after accounting for gaps between cells
      const horizontalGapSpace = (column - 1) * cellGap;
      const verticalGapSpace = (row - 1) * cellGap;

      const availableWidth = rootWidth - horizontalGapSpace;
      const availableHeight = rootHeight - verticalGapSpace;

      const cellWidth = Math.floor(availableWidth / column);
      const cellHeight = Math.floor(availableHeight / row);

      return {
        cellWidth,
        cellHeight,
        cellArea: cellWidth * cellHeight,
        column,
        row,
      };
    })
    // this reduce allows to get a best layout with the biggest area for viedo
    .reduce(
      (prev, curr) => {
        if (curr.cellArea > prev.cellArea) {
          return curr;
        }
        return prev;
      },
      {
        cellArea: 0,
        cellHeight: 0,
        cellWidth: 0,
        column: 0,
        row: 0,
      },
    );

  // calculating everything needed for our chosen layout
  const { cellWidth, cellHeight, column, row } = preferredLayout;

  const cellDimensions = [];

  let quality = VideoQuality.Video_720P;

  if (actualCount <= 4 && cellHeight >= 400) {
    // GROUP HD
    quality = VideoQuality.Video_720P;
  } else if (actualCount <= 4 && cellHeight >= 270) {
    quality = VideoQuality.Video_360P;
  } else if (actualCount > 4 && cellHeight >= 180) {
    quality = VideoQuality.Video_180P;
  }
  for (let i = 0; i < row; i++) {
    for (let j = 0; j < column; j++) {
      if (i * column + j < actualCount) {
        cellDimensions.push({
          width: cellWidth,
          height: cellHeight,
          x: j * (cellWidth + cellGap), // Add gap between columns
          y: i * (cellHeight + cellGap), // Add gap between rows
          quality,
        });
      }
    }
  }

  return cellDimensions;
}
